import React from 'react';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-base-200">
      <Header />

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-base-100 rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-sm text-base-content/70 mb-8">Last updated: July 23, 2025</p>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-xl font-bold text-primary mb-4 border-b border-base-300 pb-2">1. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-base-content mb-3">Personal Information</h3>
            <ul className="list-disc list-inside space-y-2 text-base-content/80 mb-6 ml-4">
              <li>Email address and password for account creation</li>
              <li>Payment information (processed securely through Stripe)</li>
              <li>Usage data and analytics</li>
            </ul>

            <h3 className="text-lg font-semibold text-base-content mb-3">Document Content</h3>
            <ul className="list-disc list-inside space-y-2 text-base-content/80 mb-6 ml-4">
              <li>Job descriptions you upload</li>
              <li>Resume content you upload for screening</li>
              <li>AI analysis results and scores</li>
            </ul>

            <h2 className="text-xl font-bold text-primary mb-4 border-b border-base-300 pb-2">2. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2 text-base-content/80 mb-6 ml-4">
              <li>To provide resume screening services using AI analysis</li>
              <li>To process payments and manage your credit balance</li>
              <li>To improve our AI models and service quality</li>
              <li>To communicate with you about your account and service updates</li>
              <li>To prevent fraud and ensure security</li>
            </ul>

            <h2 className="text-xl font-bold text-primary mb-4 border-b border-base-300 pb-2">3. Data Storage and Security</h2>
            <p className="text-base-content/80 leading-relaxed mb-6">
              We use industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc list-inside space-y-2 text-base-content/80 mb-6 ml-4">
              <li>All data is encrypted in transit and at rest</li>
              <li>Secure cloud infrastructure with regular security updates</li>
              <li>Access controls and authentication requirements</li>
              <li>Regular security audits and monitoring</li>
            </ul>

            <h2 className="text-xl font-bold text-primary mb-4 border-b border-base-300 pb-2">4. Data Sharing</h2>
            <p className="text-base-content/80 leading-relaxed mb-6">
              We do not sell, trade, or rent your personal information to third parties. We may share data only in these limited circumstances:
            </p>
            <ul className="list-disc list-inside space-y-2 text-base-content/80 mb-6 ml-4">
              <li>With payment processors (Stripe) for transaction processing</li>
              <li>With cloud service providers for hosting and infrastructure</li>
              <li>When required by law or to protect our rights</li>
              <li>With your explicit consent</li>
            </ul>

            <h2 className="text-xl font-bold text-primary mb-4 border-b border-base-300 pb-2">5. Data Retention</h2>
            <p className="text-base-content/80 leading-relaxed mb-6">
              We retain your data for as long as your account is active or as needed to provide services.
              You may request deletion of your account and associated data at any time.
            </p>

            <h2 className="text-xl font-bold text-primary mb-4 border-b border-base-300 pb-2">6. Your Rights</h2>
            <p className="text-base-content/80 leading-relaxed mb-6">
              You have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-base-content/80 mb-6 ml-4">
              <li>Access and review your personal data</li>
              <li>Update or correct your information</li>
              <li>Delete your account and associated data</li>
              <li>Opt out of marketing communications</li>
              <li>Request data portability</li>
            </ul>

            <h2 className="text-xl font-bold text-primary mb-4 border-b border-base-300 pb-2">7. Cookies and Tracking</h2>
            <p className="text-base-content/80 leading-relaxed mb-6">
              We use essential cookies for authentication and service functionality.
              We do not use tracking cookies or third-party analytics that compromise your privacy.
            </p>

            <h2 className="text-xl font-bold text-primary mb-4 border-b border-base-300 pb-2">8. Children's Privacy</h2>
            <p className="text-base-content/80 leading-relaxed mb-6">
              Our service is not intended for children under 13. We do not knowingly collect personal information from children under 13.
            </p>

            <h2 className="text-xl font-bold text-primary mb-4 border-b border-base-300 pb-2">9. Changes to This Policy</h2>
            <p className="text-base-content/80 leading-relaxed mb-6">
              We may update this privacy policy from time to time. We will notify you of any material changes via email or through our service.
            </p>

            <h2 className="text-xl font-bold text-primary mb-4 border-b border-base-300 pb-2">10. Contact Us</h2>
            <p className="text-base-content/80 leading-relaxed mb-6">
              If you have questions about this privacy policy or our data practices, please contact us at:
            </p>
            <div className="bg-base-200 p-4 rounded-lg">
              <p className="font-semibold">Email:</p>
              <p>support@kautilya.app</p>
              <p className="font-semibold mt-2">Address:</p>
              <p>Pacifico Technologies Private Limited<br />
              41A, 1st Floor, Road No 1, Kakateeya Hills, Madhapur, Hyderabad, Telangana, India, 500081</p>
            </div>

            <div className="alert alert-info mt-8">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>This privacy policy is effective as of July 23, 2025 and applies to all users of SuprHR.</span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPage; 